<script lang="ts">
// the organisation rows and their header share this grid: name, themes, part-time factor, API, delete
export const ORGANIZATION_COLUMNS: string = 'minmax(14rem, 1fr) minmax(16rem, 2fr) 7rem 2rem 2rem';
</script>

<script setup lang="ts">
import {computed, nextTick, type PropType, ref, toRaw} from 'vue';
import {ORGANIZATION_CHANGE_EMIT, ORGANIZATION_REMOVE_EMIT} from '@/base/cabConstants';
import {bevestigVerwijderen} from '@/base/utils';
import {
    CabObj,
    type CabOrganization,
    CabTheme,
    enum_values,
    uuidOf,
    WachttijdApiTypeEnum,
    WachttijdApiTypeEnum_name,
} from 'common';
import RowGrid from '@/views/ui/RowGrid.vue';
import IconButton from '@/views/ui/IconButton.vue';
import {Plug, Trash2} from 'lucide-vue-next';
import {ElCascader, ElDialog, ElInput, ElInputNumber, ElOption, ElSelect} from 'element-plus';

const emits = defineEmits([ORGANIZATION_REMOVE_EMIT, ORGANIZATION_CHANGE_EMIT]);
const props = defineProps({
    organization: {
        type    : Object as PropType<CabOrganization>,
        required: true,
    },
    allThemes: {
        type    : Object as PropType<CabTheme[]>,
        required: true,
    },
});
defineExpose({
    firstEdit,
});

const organization     = ref<CabOrganization>(props.organization);
const allThemesRef     = computed(() => makeCascaderOptions(props.allThemes));
const selectedThemes   = ref(props.organization.themes.map((t) => [uuidOf(t)]));
const nameField        = ref<HTMLInputElement | null>(null);
const nameEditing      = ref<boolean>(false);
const apiField         = ref<HTMLInputElement | null>(null);
const apiDialogVisible = ref<boolean>(false);

let isFirstEdit: boolean = false;

function makeCascaderOptions<T extends CabObj>(list: T[]): any {
    return list.map((o) => ({
        label: (o as any).name, 
        value: uuidOf(o),
    }));
}

function firstEdit() {
    isFirstEdit = true;
    startEditingName();
}

//==========================================
function startEditingName() {
    nameEditing.value = true;
    nextTick(() => nameField.value!.focus());
}

function stopEditingName() {
    nameEditing.value = false;
    nameField.value!.blur();
    if (isFirstEdit) {
        if (!props.organization.name.trim()) {
            onDelete();
        }
        isFirstEdit = false;
    }
}

function clearApi() {
    organization.value.wachttijdApiType = undefined;
    apiField.value?.blur();
}

async function onDeleteClick() {
    if (await bevestigVerwijderen(organization.value.name?.trim() ? `Instelling "${organization.value.name}"` : 'Deze instelling')) {
        onDelete();
    }
}

function onDelete() {
    emits(ORGANIZATION_REMOVE_EMIT, organization.value);
}

function onChangeThemes() {
    const rawSelected         = toRaw(selectedThemes.value).map((l: any) => toRaw(l)[0]);
    organization.value.themes = props.allThemes?.filter((theme) =>
        rawSelected.includes(uuidOf(theme)),
    );
    onChange();
}

function onChange() {
    emits(ORGANIZATION_CHANGE_EMIT, organization.value);
}

</script>

<template>
    <RowGrid :columns="ORGANIZATION_COLUMNS" data-testid="thema-strip">
        <input
            class="row-input"
            :class="{'row-input-on': nameEditing}"
            type="text"
            ref="nameField"
            v-model="organization.name"
            @input="onChange"
            @click="startEditingName"
            @focus="startEditingName"
            @keyup.enter="stopEditingName()"
            @blur="stopEditingName()"
            placeholder="name"
            :title="organization.name"
            data-testid="thema-name"
        />
        <div title="thema's voor deze instelling" data-testid="thema-select">
            <el-cascader class="w-full" v-model="selectedThemes" :options="allThemesRef" :props="{multiple: true}" clearable placeholder="selecteer thema's" @change="onChangeThemes"/>
        </div>
        <el-input-number
            class="!w-full"
            v-model="organization.deeltijdfactor"
            :controls="false"
            :precision="3"
            :min="0"
            :max="1"
            placeholder="deeltijd"
            title="deeltijdfactor: fte per medewerker, voor het dashboard"
            @change="onChange"
        />
        <IconButton
            :icon="Plug"
            :label="organization.kvkNumber || organization.wachttijdApiType ? 'wachttijden API (ingesteld)' : 'wachttijden API'"
            :class="organization.kvkNumber || organization.wachttijdApiType ? '!text-zzc-blauw !bg-[var(--zzc-tag-info-bg)]' : 'opacity-40 hover:opacity-100'"
            size="sm"
            data-testid="api-button"
            @click="apiDialogVisible = true"
        />
        <IconButton :icon="Trash2" label="permanent verwijderen" variant="danger" size="sm" data-testid="delete-button" @click="onDeleteClick()"/>
    </RowGrid>

    <el-dialog
        v-model="apiDialogVisible"
        width="480px"
        :close-on-press-escape="true"
        :close-on-click-modal="true"
        :title="`Wachttijden API voor ${organization.name}`"
    >
        <div class="grid grid-cols-[8rem_1fr] items-center gap-x-3 gap-y-3">
            <label class="text-xs text-zzc-muted" :for="`kvk-${uuidOf(organization)}`">kvk nummer</label>
            <el-input :id="`kvk-${uuidOf(organization)}`" v-model="organization.kvkNumber" placeholder="kvk nummer" @input="onChange"/>
            <label class="text-xs text-zzc-muted" :for="`api-type-${uuidOf(organization)}`">API type</label>
            <el-select :id="`api-type-${uuidOf(organization)}`" v-model="organization.wachttijdApiType" placeholder="API" @change="onChange" ref="apiField" clearable @keyup.delete="clearApi()">
                <el-option v-for="item in enum_values(WachttijdApiTypeEnum)" :key="item" :label="WachttijdApiTypeEnum_name(item)" :value="item"/>
            </el-select>
        </div>
    </el-dialog>
</template>
